import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

/**
 * Shared fanned PDF-thumbnail stack used by the block-page heroes
 * (related_papers, gold_card). Three offset "page" rectangles with the work's
 * first-page thumbnail on the front card and an optional red "PDF available"
 * badge.
 *
 * The component fills its parent (width/height: 100%); the consumer sizes and
 * places it via a wrapping styled element, so each variant owns its own
 * dimensions, responsive behaviour and grid placement. The page outline colour
 * is a prop applied via inline style (astroturf extracts styles at build time
 * and can't take dynamic CSS values); the badge geometry is CSS — a
 * `badgeTreatment` class variant plus the phone/tablet shrink — so it can be
 * responsive.
 */

// The prop is set only for the clickable mode and named in lowercase —
// astroturf's prop-to-class mapping misses camelCase prop names (see
// HeroCenteredHeader's StatsRow).
const Stack = styled.div<{ interactive?: 'button' }>`
  position: relative;
  width: 100%;
  height: 100%;

  &.interactive-button {
    cursor: pointer;
  }
`;

const ThumbPage = styled.div<{ position: 'back' | 'middle' | 'front' }>`
  position: absolute;
  background: var(--background-white, #ffffff);
  border: 1px solid;

  &.position-back {
    width: 90%;
    height: 86%;
    left: 5%;
    top: 14%;
  }

  &.position-middle {
    width: 95%;
    height: 90%;
    left: 2.5%;
    top: 8%;
  }

  &.position-front {
    width: 100%;
    height: 95%;
    left: 0;
    top: 0;
    overflow: hidden;
  }
`;

const ThumbImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

// Badge geometry lives in CSS (not the old inline badgeStyle prop) so it can
// shrink responsively: the phone/tablet comps (2194:15071) fit the one-line
// text in a 26px badge overhanging the small thumbnail's edges. The default
// geometry is the related_papers comp; `treatment` (lowercase — astroturf's
// prop-to-class mapping misses camelCase) selects gold_card's tighter desktop
// box.
const PdfBadge = styled.div<{ treatment?: 'gold' }>`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  top: 25px;
  left: -10px;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  height: 38px;
  padding: 0 var(--spacing-xs-12, 12px);
  border-radius: var(--border-radius-radius-sm, 4px);
  background: var(--brand-colors-error-900, #920000);
  color: var(--text-white, #ffffff);

  @include breakpoint(small, medium) {
    top: 18px;
    left: -5px;
    gap: var(--spacing-xs-4, 4px);
    height: 26px;
    padding: 0 6px;
  }

  &.treatment-gold {
    top: 18px;
    left: -8px;
    height: 34px;
    padding: 0 10px;
    border-radius: 6px;

    @include breakpoint(small, medium) {
      top: 14px;
      left: -5px;
      height: 26px;
      padding: 0 6px;
    }
  }
`;

// "PDF available" — always ONE line (nowrap): Figma body/medium-bold (DM Sans
// SemiBold, 16/20) on desktop, dropping to 12px on phone/tablet where the
// thumbnail is too narrow for the 16px text (2194:15073). The DS Body has no
// working `md` bold variant (`<Body size="md" bold>` falls back to weight
// 400), so bind the tokens directly. Colour inherits from PdfBadge (white).
const BadgeText = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-md-bold-size, 16px);
  line-height: var(--type-body-md-bold-line-height, 20px);
  font-weight: var(--type-body-md-bold-font-weight, 600);
  white-space: nowrap;

  @include breakpoint(small, medium) {
    font-size: 12px;
    line-height: 19px;
  }
`;

// The check icon scales with the badge (24px desktop / 17px phone-tablet, 20px
// for gold's tighter desktop box). The DS Icon sets its size as an inline
// style, so the responsive swap is two icons toggled at the breakpoint rather
// than a CSS override.
const BadgeIconLarge = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: inline-flex;

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const BadgeIconSmall = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small, medium) {
    display: inline-flex;
  }
`;

// Default matches the related_papers hero, the first consumer, so its render
// is unchanged by the extraction.
const DEFAULT_PAGE_BORDER_COLOR = 'var(--brand-colors-neutral-600, #b1b1ba)';

export type HeroThumbnailStackProps = {
  /** First-page thumbnail shown on the front card. */
  thumbnailUrl?: string;
  /** Whether to render the red "PDF available" badge. */
  hasPdf?: boolean;
  /** Outline colour for the three page rectangles. */
  pageBorderColor?: string;
  /** Badge geometry: related_papers' (default) or gold_card's tighter box. */
  badgeTreatment?: 'gold';
  /**
   * Makes the whole stack a keyboard-reachable button, like royal_blue's
   * thumbnail (#51656): the heroes pass their bulk-download opener recording
   * SwpCta.TopCardBulkDownloadThumbnail, so the thumbnail surface stays
   * distinguishable from the package button in the warehouse across arms.
   */
  onClick?: () => void;
};

const CLICK_LABEL = 'Download full PDF package';

export const HeroThumbnailStack: React.FC<HeroThumbnailStackProps> = ({
  thumbnailUrl,
  hasPdf = false,
  pageBorderColor = DEFAULT_PAGE_BORDER_COLOR,
  badgeTreatment,
  onClick,
}) => (
  <Stack
    interactive={onClick ? 'button' : undefined}
    {...(onClick
      ? {
          role: 'button',
          tabIndex: 0,
          'aria-label': CLICK_LABEL,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
        }
      : {})}
  >
    <ThumbPage position="back" style={{ borderColor: pageBorderColor }} />
    <ThumbPage position="middle" style={{ borderColor: pageBorderColor }} />
    <ThumbPage position="front" style={{ borderColor: pageBorderColor }}>
      {thumbnailUrl && <ThumbImage src={thumbnailUrl} alt="" />}
    </ThumbPage>
    {hasPdf && (
      <PdfBadge treatment={badgeTreatment}>
        <BadgeIconLarge>
          <Icon
            name="check_circle"
            size={badgeTreatment === 'gold' ? 20 : 24}
          />
        </BadgeIconLarge>
        <BadgeIconSmall>
          <Icon name="check_circle" size={17} />
        </BadgeIconSmall>
        <BadgeText>PDF available</BadgeText>
      </PdfBadge>
    )}
  </Stack>
);

export default HeroThumbnailStack;
